import { useState, useEffect, useRef } from 'react';
import { agentsAPI, chatAPI, billingAPI } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import UpgradeBanner from '../components/UpgradeBanner';
import {
  Bot, Plus, Trash2, Edit2, Play, Loader2, Send, MessageSquare,
  Globe, Mic, Sparkles, Phone, X, AlertTriangle, Volume2, Square, Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Fallback OpenAI voices (if API call fails)
const OPENAI_FALLBACK_VOICES = [
  { id: 'alloy', gender: 'Female', style: 'Neutral' },
  { id: 'ash', gender: 'Male', style: 'Calm' },
  { id: 'ballad', gender: 'Male', style: 'Warm' },
  { id: 'coral', gender: 'Female', style: 'Bright' },
  { id: 'echo', gender: 'Male', style: 'Clear' },
  { id: 'sage', gender: 'Female', style: 'Soft' },
  { id: 'shimmer', gender: 'Female', style: 'Lively' },
  { id: 'verse', gender: 'Male', style: 'Expressive' },
];

// Languages — All 14 Indian languages supported across all plans
const ALL_LANGUAGES = ['Hindi', 'Hinglish', 'English', 'Tamil', 'Gujarati', 'Bengali', 'Marathi', 'Telugu', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese', 'Urdu'];
const STANDARD_LANGUAGES = ALL_LANGUAGES;
const USE_CASES = ['sales', 'support', 'lead_qualification', 'appointment_booking', 'surveys', 'reminders', 'general'];
const PERSONALITIES = ['professional', 'friendly', 'casual', 'formal', 'empathetic', 'enthusiastic'];
const TONES = ['friendly', 'confident', 'calm', 'energetic', 'warm', 'neutral', 'empathetic'];

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [testAgent, setTestAgent] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [agentLimit, setAgentLimit] = useState({ count: 0, limit: 1, planName: 'Free', planId: 'free' });
  const [availableVoices, setAvailableVoices] = useState(OPENAI_FALLBACK_VOICES);
  const [voiceProvider, setVoiceProvider] = useState('openai');
  const [greetingVoices, setGreetingVoices] = useState([]);
  
  // Voice preview state
  const [previewPlaying, setPreviewPlaying] = useState(null); // voice id currently playing
  const [previewLoading, setPreviewLoading] = useState(null); // voice id currently loading
  const previewAudioRef = useRef(null);
  
  // AI Fill state
  const [aiFillEnabled, setAiFillEnabled] = useState(false);
  const [aiFillDescription, setAiFillDescription] = useState('');
  const [aiFillLoading, setAiFillLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '', description: '', voice: 'alloy', language: 'Hindi',
    personality: 'professional', tone: 'friendly', system_prompt: '',
    greeting_message: 'Namaste! Main aapki kaise help kar sakta hoon?', training_script: '',
    use_case: 'general', escalation_enabled: false, escalation_number: '',
    fallback_language_detection: false, vad_sensitivity: 'medium',
    greeting_voice: 'auto'
  });

  // Get available languages based on plan
  const getAvailableLanguages = () => {
    if (agentLimit.planId === 'standard') {
      return STANDARD_LANGUAGES;
    }
    return ALL_LANGUAGES;
  };

  const getPlanProviderBadge = () => {
    const planId = agentLimit.planId;
    if (planId === 'standard') {
      return { text: 'DotCall Voice', color: 'emerald' };
    } else if (planId === 'premium') {
      return { text: 'DotCall Premium', color: 'amber' };
    } else if (planId === 'enterprise') {
      return { text: 'DotCall Enterprise', color: 'amber' };
    }
    return { text: 'DotCall Voice', color: 'slate' };
  };

  useEffect(() => { loadAgents(); loadLimits(); loadGreetingVoices(); }, []);

  // Load greeting voice options
  const loadGreetingVoices = async () => {
    try {
      const res = await axios.get(`${API_BASE}/agents/greeting-voices`);
      setGreetingVoices(res.data || []);
    } catch (err) {
      console.error('Failed to load greeting voices', err);
      // Fallback greeting voices
      setGreetingVoices([
        { id: 'auto', label: 'Auto (match agent voice gender)', gender: 'auto' },
        { id: 'nova', label: 'Nova', gender: 'Female', style: 'Warm, natural' },
        { id: 'shimmer', label: 'Shimmer', gender: 'Female', style: 'Expressive, clear' },
        { id: 'echo', label: 'Echo', gender: 'Male', style: 'Deep, clear' },
        { id: 'onyx', label: 'Onyx', gender: 'Male', style: 'Authoritative, rich' },
        { id: 'alloy', label: 'Alloy', gender: 'Neutral', style: 'Balanced, versatile' },
        { id: 'fable', label: 'Fable', gender: 'Male', style: 'Warm, storytelling' },
      ]);
    }
  };

  // Load voices whenever plan changes
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const res = await axios.get(`${API_BASE}/voice/voices`, {
          params: { plan: agentLimit.planId }
        });
        const voices = res.data?.voices || OPENAI_FALLBACK_VOICES;
        setAvailableVoices(voices);
        setVoiceProvider(res.data?.provider || 'openai');
        // If current form.voice not in list, reset to default
        setForm(prev => {
          const voiceIds = voices.map(v => v.id);
          if (!voiceIds.includes(prev.voice)) {
            return { ...prev, voice: res.data?.default || voices[0]?.id || 'alloy' };
          }
          return prev;
        });
      } catch (err) {
        console.error('Failed to load voices', err);
        setAvailableVoices(OPENAI_FALLBACK_VOICES);
        setVoiceProvider('openai');
      }
    };
    loadVoices();
  }, [agentLimit.planId]);

  const loadLimits = async () => {
    try {
      const res = await billingAPI.usage();
      setAgentLimit({
        count: res.data.agent_count || 0,
        limit: res.data.agent_limit,
        planName: res.data.plan?.name || 'Free',
        planId: res.data.plan?.id || 'free'
      });
    } catch {}
  };

  const loadAgents = async () => {
    try {
      const res = await agentsAPI.list();
      setAgents(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const resetForm = () => setForm({
    name: '', description: '',
    voice: voiceProvider === 'grok' ? 'eve' : 'alloy',
    language: 'Hindi',
    personality: 'professional', tone: 'friendly', system_prompt: '',
    greeting_message: agentLimit.planId === 'standard'
      ? 'Namaste! Main aapki kaise help kar sakta hoon?'
      : 'Hello! How can I help you today?',
    training_script: '',
    use_case: 'general', escalation_enabled: false, escalation_number: '',
    fallback_language_detection: false, vad_sensitivity: 'medium',
    greeting_voice: 'auto'
  });

  // ── Voice Preview ──
  const handleVoicePreview = async (voiceId) => {
    // Stop currently playing audio
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    
    // If clicking the same voice that's playing, just stop
    if (previewPlaying === voiceId) {
      setPreviewPlaying(null);
      return;
    }
    
    setPreviewLoading(voiceId);
    setPreviewPlaying(null);
    
    try {
      const res = await agentsAPI.voicePreview(voiceId, form.language);
      const audioBlob = new Blob([res.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      previewAudioRef.current = audio;
      
      audio.onended = () => {
        setPreviewPlaying(null);
        URL.revokeObjectURL(audioUrl);
      };
      audio.onerror = () => {
        setPreviewPlaying(null);
        toast.error('Failed to play audio');
      };
      
      await audio.play();
      setPreviewPlaying(voiceId);
    } catch (err) {
      console.error('Voice preview failed:', err);
      toast.error(err?.response?.data?.detail || 'Voice preview failed — try again');
    } finally {
      setPreviewLoading(null);
    }
  };

  // ── AI Auto-fill ──
  const handleAiFill = async () => {
    if (!aiFillDescription.trim()) {
      toast.error('Please describe your business first');
      return;
    }
    
    setAiFillLoading(true);
    try {
      const res = await agentsAPI.aiFill({
        business_description: aiFillDescription,
        language: form.language,
        use_case: form.use_case
      });
      const data = res.data;
      
      setForm(prev => ({
        ...prev,
        name: data.name || prev.name,
        description: data.description || prev.description,
        greeting_message: data.greeting_message || prev.greeting_message,
        system_prompt: data.system_prompt || prev.system_prompt,
        training_script: data.training_script || prev.training_script,
        personality: data.personality || prev.personality,
        tone: data.tone || prev.tone,
      }));
      
      toast.success('AI filled agent details! Review and adjust as needed.');
    } catch (err) {
      console.error('AI fill failed:', err);
      toast.error('AI fill failed — please try again');
    } finally {
      setAiFillLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Agent name is required'); return; }
    setSaving(true);
    try {
      if (editAgent) {
        await agentsAPI.update(editAgent.id, form);
        toast.success('Agent updated');
      } else {
        await agentsAPI.create(form);
        toast.success('Agent created');
      }
      setShowCreate(false);
      setEditAgent(null);
      resetForm();
      loadAgents();
      loadLimits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save agent');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await agentsAPI.delete(id);
      toast.success('Agent deleted');
      loadAgents();
      loadLimits();
    } catch (err) { toast.error('Failed to delete agent'); }
  };

  const handleEdit = (agent) => {
    const availableLangs = agentLimit.planId === 'standard' ? STANDARD_LANGUAGES : ALL_LANGUAGES;
    const agentLang = agent.language || 'Hindi';
    
    // If editing agent has language not available in current plan, reset to first available
    const finalLang = availableLangs.includes(agentLang) ? agentLang : availableLangs[0];
    
    // If current voice not in available voices (e.g. plan switched), reset to default
    const voiceIds = availableVoices.map(v => v.id);
    const defaultVoice = voiceProvider === 'grok' ? 'eve' : 'alloy';
    const finalVoice = voiceIds.includes(agent.voice) ? agent.voice : defaultVoice;
    
    setForm({
      name: agent.name, description: agent.description || '', voice: finalVoice,
      language: finalLang, personality: agent.personality || 'professional',
      tone: agent.tone || 'friendly', system_prompt: agent.system_prompt || '',
      greeting_message: agent.greeting_message || '', training_script: agent.training_script || '',
      use_case: agent.use_case || 'general',
      escalation_enabled: agent.escalation_enabled || false, escalation_number: agent.escalation_number || '',
      fallback_language_detection: agent.fallback_language_detection || false,
      vad_sensitivity: agent.vad_sensitivity || 'medium',
      greeting_voice: agent.greeting_voice || 'auto'
    });
    setEditAgent(agent);
    setShowCreate(true);
  };

  const handleToggleStatus = async (agent) => {
    const newStatus = agent.status === 'active' ? 'draft' : 'active';
    try {
      await agentsAPI.update(agent.id, { status: newStatus });
      loadAgents();
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (err) { toast.error('Failed to update status'); }
  };

  const handleTestAgent = (agent) => {
    setTestAgent(agent);
    setChatMessages([{ role: 'assistant', content: agent.greeting_message || 'Hello! How can I help you?' }]);
    setChatSessionId(null);
    setChatInput('');
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await chatAPI.send({ agent_id: testAgent.id, message: msg, session_id: chatSessionId });
      setChatSessionId(res.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error: Could not get response. Please try again.' }]);
    } finally { setChatLoading(false); }
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  const atLimit = agentLimit.limit !== -1 && agentLimit.count >= agentLimit.limit;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="agents-page">
      {/* Upgrade Banner for Standard Plan Users */}
      <UpgradeBanner />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">AI Agents</h1>
          <p className="text-slate-400 mt-1">
            {agentLimit.limit === -1
              ? `${agentLimit.count} agents (unlimited)`
              : `${agentLimit.count} / ${agentLimit.limit} agents on ${agentLimit.planName} plan`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {atLimit && (
            <span className="text-xs text-amber-400 flex items-center gap-1" data-testid="agent-limit-warning">
              <AlertTriangle className="w-3.5 h-3.5" /> Limit reached
            </span>
          )}
          <Button
            onClick={() => { resetForm(); setEditAgent(null); setShowCreate(true); }}
            disabled={atLimit}
            data-testid="create-agent-button"
            className={`${atLimit ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]'}`}
          >
            <Plus className="w-4 h-4 mr-2" /> New Agent
          </Button>
        </div>
      </div>

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="no-agents">
          <Bot className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No agents yet</h3>
          <p className="text-slate-400 mb-6">Create your first AI voice agent to get started</p>
          <Button onClick={() => { resetForm(); setShowCreate(true); }} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Create Agent
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {agents.map((agent, i) => (
            <div
              key={agent.id}
              className="glass-card rounded-xl p-5 hover:-translate-y-1 transition-transform duration-300 animate-fade-in"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`agent-card-${agent.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold font-['Outfit']">{agent.name}</h3>
                    <span className="text-xs text-slate-500 capitalize">{agent.use_case?.replace('_', ' ')}</span>
                  </div>
                </div>
                <Badge
                  variant={agent.status === 'active' ? 'default' : 'secondary'}
                  className={agent.status === 'active' ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30' : 'bg-slate-800 text-slate-400 border-slate-700'}
                  data-testid={`agent-status-${agent.id}`}
                >
                  {agent.status}
                </Badge>
              </div>

              <p className="text-sm text-slate-400 mb-4 line-clamp-2">{agent.description || 'No description'}</p>

              <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                <span className="flex items-center gap-1"><Mic className="w-3 h-3" /> {agent.voice}</span>
                <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {agent.language}</span>
                <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> {agent.personality}</span>
                {agent.greeting_audio_hash ? (
                  <span className="flex items-center gap-1 text-emerald-500" title="Instant greeting audio ready">
                    <Phone className="w-3 h-3" /> Instant
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-500 cursor-pointer hover:text-amber-400" 
                    title="Click to generate instant greeting audio"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        toast.info('Generating greeting audio...');
                        await agentsAPI.regenerateGreeting(agent.id);
                        toast.success('Greeting audio generated!');
                        loadAgents();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to generate audio. Try again later.');
                      }
                    }}
                  >
                    <Phone className="w-3 h-3" /> Gen Audio
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                <Button size="sm" variant="ghost" onClick={() => handleTestAgent(agent)} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 text-xs" data-testid={`test-agent-${agent.id}`}>
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Test
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleEdit(agent)} className="text-slate-400 hover:text-white hover:bg-white/5 text-xs" data-testid={`edit-agent-${agent.id}`}>
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(agent)} className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid={`toggle-agent-${agent.id}`}>
                  <Play className="w-3.5 h-3.5 mr-1" /> {agent.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(agent.id)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-xs ml-auto" data-testid={`delete-agent-${agent.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) { setEditAgent(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl bg-[#0B1221] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="agent-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] flex items-center gap-3">
              {editAgent ? 'Edit Agent' : 'Create New Agent'}
              <Badge className={`text-xs px-2.5 py-0.5 bg-${getPlanProviderBadge().color}-600/20 text-${getPlanProviderBadge().color}-300 border-${getPlanProviderBadge().color}-500/30`}>
                <Sparkles className="w-3 h-3 mr-1" />
                {getPlanProviderBadge().text}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300 text-sm mb-2 block">Agent Name *</Label>
              <Input value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder="Sales Agent Pro" data-testid="agent-name-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300 text-sm mb-2 block">Description</Label>
              <Textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} placeholder="Describe what this agent does..." data-testid="agent-description-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 min-h-[80px]" />
            </div>

            <div>
              <Label className="text-slate-300 text-sm mb-2 block">
                Voice
                {voiceProvider === 'grok' && (
                  <span className="text-xs text-emerald-400 ml-2">(DotCall Voice — {availableVoices.length} voices)</span>
                )}
              </Label>
              <div className="space-y-1.5">
                {availableVoices.map(v => (
                  <div key={v.id} className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all cursor-pointer ${
                    form.voice === v.id
                      ? 'border-emerald-500/50 bg-emerald-600/10'
                      : 'border-white/5 bg-slate-950 hover:border-white/10'
                  }`} onClick={() => updateForm('voice', v.id)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${form.voice === v.id ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      <span className="capitalize font-medium text-sm text-white">{v.id}</span>
                      <span className="text-xs text-slate-400">({v.gender}{v.style ? ` · ${v.style}` : ''})</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-emerald-400 hover:bg-emerald-600/10"
                      onClick={(e) => { e.stopPropagation(); handleVoicePreview(v.id); }}
                      disabled={previewLoading === v.id}
                    >
                      {previewLoading === v.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : previewPlaying === v.id ? (
                        <Square className="w-3 h-3 fill-current" />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
              {voiceProvider === 'grok' && (
                <p className="text-xs text-slate-500 mt-1.5">Click <Volume2 className="w-3 h-3 inline" /> to preview voice in {form.language}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">
                Language
                {agentLimit.planId === 'standard' && (
                  <span className="text-xs text-emerald-400 ml-2">(Hindi-first · auto-switches to English if caller speaks English)</span>
                )}
              </Label>
              <Select value={form.language} onValueChange={(v) => updateForm('language', v)}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="agent-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 max-h-60">
                  {getAvailableLanguages().map(l => <SelectItem key={l} value={l} className="text-white hover:bg-white/5">{l}</SelectItem>)}
                </SelectContent>
              </Select>
              {agentLimit.planId === 'standard' && (
                <p className="text-xs text-slate-500 mt-1.5">AI primarily speaks Hindi/Hinglish. It will auto-switch to English only if the caller clearly speaks in English.</p>
              )}
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Personality</Label>
              <Select value={form.personality} onValueChange={(v) => updateForm('personality', v)}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="agent-personality-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {PERSONALITIES.map(p => <SelectItem key={p} value={p} className="text-white hover:bg-white/5 capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Tone</Label>
              <Select value={form.tone} onValueChange={(v) => updateForm('tone', v)}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="agent-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {TONES.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-white/5 capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Use Case</Label>
              <Select value={form.use_case} onValueChange={(v) => updateForm('use_case', v)}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="agent-usecase-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {USE_CASES.map(u => <SelectItem key={u} value={u} className="text-white hover:bg-white/5 capitalize">{u.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Greeting Message</Label>
              <Input value={form.greeting_message} onChange={(e) => updateForm('greeting_message', e.target.value)} placeholder="Hello! How can I help?" data-testid="agent-greeting-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              <p className="text-xs text-slate-500 mt-1">AI speaks this as its first message on every call.</p>
            </div>

            {/* AI Training Section */}
            <div className="md:col-span-2 mt-2">
              <div className="rounded-lg border border-amber-500/20 bg-amber-600/5 p-4 mb-4">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-amber-300 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Training</h4>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-amber-300/80 cursor-pointer">Fill by AI</Label>
                    <Switch
                      checked={aiFillEnabled}
                      onCheckedChange={setAiFillEnabled}
                      className="data-[state=checked]:bg-amber-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  {aiFillEnabled
                    ? 'Describe your business below and AI will auto-generate all agent fields.'
                    : 'Train your agent by providing instructions, a conversation script, and knowledge base content.'
                  }
                </p>
              </div>
            </div>

            {aiFillEnabled && (
              <div className="md:col-span-2">
                <Label className="text-amber-300 text-sm mb-2 block flex items-center gap-2">
                  <Wand2 className="w-4 h-4" /> Describe Your Business
                </Label>
                <Textarea
                  value={aiFillDescription}
                  onChange={(e) => setAiFillDescription(e.target.value)}
                  placeholder={"Describe your business, products/services, pricing, and what you want the AI agent to do.\n\nExample:\nWe are MeraMarket, an online grocery delivery service in Mumbai.\nWe deliver in 30-60 minutes, minimum order ₹200.\nFree delivery above ₹500. Currently running 20% off for new customers.\nThe agent should call potential customers, introduce our service, and get them to place their first order."}
                  className="bg-slate-950 border-amber-500/20 text-white placeholder:text-slate-600 min-h-[140px] text-sm"
                />
                <Button
                  type="button"
                  onClick={handleAiFill}
                  disabled={aiFillLoading || !aiFillDescription.trim()}
                  className="mt-3 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium px-6"
                >
                  {aiFillLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Generate Agent with AI</>
                  )}
                </Button>
                {aiFillLoading && (
                  <p className="text-xs text-amber-400/60 mt-2">AI is crafting your agent... this takes 5-10 seconds.</p>
                )}
              </div>
            )}

            <div className="md:col-span-2">
              <Label className="text-slate-300 text-sm mb-2 block">System Prompt (Agent Instructions)</Label>
              <Textarea value={form.system_prompt} onChange={(e) => updateForm('system_prompt', e.target.value)}
                placeholder={"Example: You are Sarah, a sales agent for MeraMarket grocery delivery.\n\nYour goal is to:\n1. Introduce yourself and the company\n2. Ask if they're interested in grocery delivery\n3. Explain our pricing: Free delivery on orders above Rs 500\n4. Offer a 20% discount coupon for first order\n5. If interested, ask for their pincode to check availability\n6. If not interested, thank them politely and end the call\n\nKey info:\n- Company: MeraMarket\n- Delivery time: 30-60 minutes\n- Minimum order: Rs 200\n- Areas: Mumbai, Delhi, Bangalore"}
                data-testid="agent-prompt-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 min-h-[180px] text-sm" />
              <p className="text-xs text-slate-500 mt-1.5">Tell the AI who it is, what to say, what to avoid, and how to handle different scenarios.</p>
            </div>

            <div className="md:col-span-2">
              <Label className="text-slate-300 text-sm mb-2 block">Conversation Script (Example Dialogue)</Label>
              <Textarea value={form.training_script} onChange={(e) => updateForm('training_script', e.target.value)}
                placeholder={"Example conversation flow:\n\nAgent: Hi! Am I speaking with [name]? This is Sarah from MeraMarket.\nCustomer: Yes, who is this?\nAgent: I'm calling from MeraMarket - we deliver fresh groceries to your doorstep in under 60 minutes. I wanted to share an exclusive offer with you.\nCustomer: Okay, tell me more.\nAgent: We're offering 20% off on your first order! Plus free delivery on orders above 500 rupees. Would you be interested?\nCustomer: What do you deliver?\nAgent: Everything from fruits, vegetables, dairy, snacks, to household essentials. Over 5000 products available!\nCustomer: Sounds good, how do I order?\nAgent: I can send you a link right now! Let me just confirm your pincode to check if we deliver in your area."}
                data-testid="agent-script-input"
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 min-h-[160px] text-sm" />
              <p className="text-xs text-slate-500 mt-1.5">Provide example conversations so the AI learns your preferred flow and responses.</p>
            </div>

            <div className="md:col-span-2 flex items-center justify-between p-4 rounded-lg bg-slate-950/50 border border-white/5">
              <div>
                <Label className="text-white text-sm">Call Escalation</Label>
                <p className="text-xs text-slate-500 mt-0.5">Transfer to human agent when needed</p>
              </div>
              <Switch checked={form.escalation_enabled} onCheckedChange={(v) => updateForm('escalation_enabled', v)} data-testid="agent-escalation-switch" />
            </div>
            {form.escalation_enabled && (
              <div className="md:col-span-2">
                <Label className="text-slate-300 text-sm mb-2 block">Escalation Phone Number</Label>
                <Input value={form.escalation_number} onChange={(e) => updateForm('escalation_number', e.target.value)} placeholder="+1234567890" data-testid="agent-escalation-number"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
            )}

            <div className="md:col-span-2 flex items-center justify-between p-4 rounded-lg bg-slate-950/50 border border-white/5">
              <div>
                <Label className="text-white text-sm">Fallback Language Detection</Label>
                <p className="text-xs text-slate-500 mt-0.5">AI detects caller's language and automatically switches to it during the conversation</p>
              </div>
              <Switch checked={form.fallback_language_detection} onCheckedChange={(v) => updateForm('fallback_language_detection', v)} data-testid="agent-fallback-lang-switch" />
            </div>

            {/* VAD Sensitivity (DotCall Voice) */}
            {agentLimit.planId === 'standard' && (
              <div className="md:col-span-2">
                <Label className="text-slate-300 text-sm mb-2 block">
                  Voice Detection Sensitivity (VAD)
                  <span className="text-xs text-emerald-400 ml-2">(DotCall Voice)</span>
                </Label>
                <Select value={form.vad_sensitivity} onValueChange={(v) => updateForm('vad_sensitivity', v)}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="agent-vad-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    <SelectItem value="low" className="text-white hover:bg-white/5">
                      <span className="font-medium">Low</span>
                      <span className="text-xs text-slate-400 ml-2">- Conservative (rejects background noise)</span>
                    </SelectItem>
                    <SelectItem value="medium" className="text-white hover:bg-white/5">
                      <span className="font-medium">Medium</span>
                      <span className="text-xs text-slate-400 ml-2">- Balanced (recommended)</span>
                    </SelectItem>
                    <SelectItem value="high" className="text-white hover:bg-white/5">
                      <span className="font-medium">High</span>
                      <span className="text-xs text-slate-400 ml-2">- Aggressive (picks up soft/distant speech)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1.5">
                  Controls how sensitive the AI is to detecting the start of speech. If the AI isn't picking up your voice, try <span className="text-amber-400">High</span>. If it triggers on background noise, try <span className="text-amber-400">Low</span>.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-agent-button"
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editAgent ? 'Update Agent' : 'Create Agent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Chat Dialog */}
      <Dialog open={!!testAgent} onOpenChange={(open) => { if (!open) setTestAgent(null); }}>
        <DialogContent className="max-w-lg bg-[#0B1221] border-white/10 text-white p-0 gap-0" data-testid="chat-dialog">
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-600/20 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white font-['Outfit']">Test: {testAgent?.name}</h3>
                <span className="text-xs text-slate-500">Powered by DotCall AI</span>
              </div>
            </div>
          </div>

          <div className="h-80 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-amber-600 text-white rounded-br-sm'
                    : 'bg-slate-800 text-slate-200 rounded-bl-sm'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 px-4 py-3 rounded-xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-white/5">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
                autoFocus
                data-testid="chat-input"
                className="flex-1 h-10 rounded-md bg-slate-950 border border-white/10 text-white placeholder:text-slate-600 px-3 text-sm focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50"
              />
              <Button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()} data-testid="chat-send-button"
                className="bg-amber-600 hover:bg-amber-700 text-white px-3">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
